import { useState } from 'react';
import { Plus, Trash2, ListChecks } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { MoneyInput } from '../../components/common/MoneyInput';
import { EmptyState } from '../../components/common/EmptyState';
import { formatMoney } from '../../lib/money';
import { JOB_STATUSES, JOB_PRIORITIES, STATUS_FLOW, INPUT } from './config';
import { useJobItems } from './hooks';
import { PhotoGrid } from '../../components/common/PhotoGrid';
import type { JobRow } from './hooks';

interface Props {
  job: JobRow | null;
  businessId: string;
  userId: string;
  onClose: () => void;
  onAdvance: (id: string, status: string) => void;
}

export function JobDetailPanel({ job, businessId, userId, onClose, onAdvance }: Props) {
  const { items, total, addItem, removeItem } = useJobItems(job?.id ?? null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [itemType, setItemType] = useState<'labour' | 'material' | 'expense'>('labour');
  const [itemDesc, setItemDesc] = useState('');
  const [itemQty, setItemQty] = useState('1');
  const [itemPrice, setItemPrice] = useState('');

  if (!job) return null;
  const st = JOB_STATUSES[job.status] ?? JOB_STATUSES.new;
  const pr = JOB_PRIORITIES[job.priority ?? 'medium'] ?? JOB_PRIORITIES.medium;
  const isActive = STATUS_FLOW.includes(job.status) && job.status !== 'completed';
  const nextIdx = STATUS_FLOW.indexOf(job.status) + 1;
  const nextStatus = nextIdx > 0 && nextIdx < STATUS_FLOW.length ? STATUS_FLOW[nextIdx] : null;

  const handleAddItem = async () => {
    // Q1.5 boundary: addItem accepts unit_price as number; itemPrice is canonical after MoneyInput blur.
    // work_order_items.amount is a GENERATED ALWAYS column (quantity * unit_price); PostgREST strips it on insert.
    const ok = await addItem({ type: itemType, description: itemDesc, quantity: Number(itemQty) || 1, unit_price: Number(itemPrice) || 0 }, businessId, userId);
    if (ok) { setShowAddItem(false); setItemDesc(''); setItemQty('1'); setItemPrice(''); }
  };

  return (
    <SlidePanel isOpen={!!job} onClose={onClose} title={job.title} subtitle={`#${job.id.slice(0, 8)}`} width={560}>
      {/* Header badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: st.bg, color: st.fg }}>{st.label}</span>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500, background: pr.bg, color: pr.fg }}>{pr.label} priority</span>
        {isActive && nextStatus && (
          <button onClick={() => onAdvance(job.id, nextStatus)} style={{
            marginLeft: 'auto', padding: '5px 14px', borderRadius: 6, border: 'none',
            background: palette.accent, color: palette.white, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Mark {JOB_STATUSES[nextStatus]?.label ?? nextStatus}</button>
        )}
      </div>

      {/* Status pipeline */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {STATUS_FLOW.map(s => {
          const active = STATUS_FLOW.indexOf(job.status) >= STATUS_FLOW.indexOf(s);
          return <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: active ? palette.accent : palette.g200 }} />;
        })}
      </div>

      {/* Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, fontSize: 13 }}>
        <div><span style={{ color: palette.g400 }}>Client</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.client_name ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Assigned</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.assigned_name ?? 'Unassigned'}</div></div>
        <div><span style={{ color: palette.g400 }}>Scheduled</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.scheduled_date ?? '—'} {job.scheduled_time ?? ''}</div></div>
        <div><span style={{ color: palette.g400 }}>Location</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{job.location ?? job.address ?? '—'}</div></div>
      </div>
      {job.description && <div style={{ fontSize: 13, color: palette.g600, marginBottom: 16, padding: '10px 12px', background: palette.g50, borderRadius: 6 }}>{job.description}</div>}

      {/* Line items */}
      <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700 }}>Line Items</span>
          <button onClick={() => setShowAddItem(!showAddItem)} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 4, border: 'none', background: palette.accent, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={12} /> Add</button>
        </div>

        {showAddItem && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <select style={{ ...INPUT, width: 90 }} value={itemType} onChange={e => setItemType(e.target.value as typeof itemType)}>
              <option value="labour">Labour</option><option value="material">Material</option><option value="expense">Expense</option>
            </select>
            <input style={{ ...INPUT, flex: 1 }} value={itemDesc} onChange={e => setItemDesc(e.target.value)} placeholder="Description" />
            <input type="number" style={{ ...INPUT, width: 50 }} value={itemQty} onChange={e => setItemQty(e.target.value)} placeholder="Qty" />
            <div style={{ width: 110 }}><MoneyInput value={itemPrice} onChange={setItemPrice} /></div>
            <button onClick={handleAddItem} disabled={!itemDesc.trim()} style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: palette.green, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
          </div>
        )}

        {items.length === 0 ? (
          <EmptyState
            icon={<ListChecks size={20} />}
            title="No line items yet"
            subtitle="Add labour, materials, or expenses to track what's billable on this job."
            actionLabel={showAddItem ? undefined : 'Add line item'}
            onAction={showAddItem ? undefined : () => setShowAddItem(true)}
          />
        ) : (
          <div>
            {items.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${palette.g100}` }}>
                <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: palette.g100, color: palette.g500, textTransform: 'capitalize' }}>{item.type}</span>
                <span style={{ flex: 1, fontSize: 13, color: palette.g700 }}>{item.description}</span>
                <span style={{ fontSize: 12, color: palette.g500 }}>{item.quantity} × {formatMoney(item.unit_price)}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: palette.g800, width: 70, textAlign: 'right' }}>{formatMoney(item.amount)}</span>
                <button onClick={() => removeItem(item.id, businessId, userId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.g400, padding: 2 }}><Trash2 size={13} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, fontSize: 14, fontWeight: 700, color: palette.g800 }}>
              Total: {formatMoney(total)}
            </div>
          </div>
        )}
      </div>

      {/* Photos */}
      <PhotoGrid entityType="work_order" entityId={job.id} tableName="work_order_photos" foreignKey="work_order_id" phases={['before', 'during', 'after']} />

      {/* Linked entities */}
      {(job.project_id || job.booking_id || job.invoice_id) && (
        <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: palette.g400, textTransform: 'uppercase' }}>Linked</span>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {job.project_id && <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#8B5CF620', color: '#8B5CF6' }}>Project</span>}
            {job.booking_id && <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#CA501020', color: '#CA5010' }}>Booking</span>}
            {job.invoice_id && <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: palette.greenWash, color: palette.green }}>Invoice</span>}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
      </div>
    </SlidePanel>
  );
}
